import React, { useState, useEffect } from 'react';
import { Box, Grid, Paper, Typography, List, ListItem, ListItemText, TextField, Button, Card, CardContent, CardMedia, Input, ListItemIcon, FormControl, Select, MenuItem, InputAdornment, InputLabel, IconButton, Tooltip, Collapse } from '@mui/material';
import { styled } from '@mui/material/styles';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LocationSelector from '../components/LocationSelector';
import { useCart } from '../contexts/CartContext';
import { 
  MenuBook,
  School,
  AutoStories,
  Book,
  Psychology,
  TravelExplore,
  Science,
  Gavel,
  Favorite,
  Public,
  EmojiObjects,
  Search,
  Store,
  LocationOn,
  Close,
  ExpandLess,
  ExpandMore
} from '@mui/icons-material';

// Add keyframes for cursor blinking
const keyframes = `
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }
`;

// Inject the keyframes into the document
const injectGlobalStyles = () => {
  const styleEl = document.createElement('style');
  styleEl.innerHTML = keyframes;
  document.head.appendChild(styleEl);
  return () => document.head.removeChild(styleEl);
};

// Styled components
const Sidebar = styled(Paper)(({ theme }) => ({
  position: 'sticky',
  top: theme.spacing(2),
  maxWidth: '260px',
  width: '100%',
}));

const CategoryList = styled(List)(({ theme }) => ({
  maxHeight: '50vh',
  overflow: 'auto',
  scrollbarWidth: 'none', // Firefox
  msOverflowStyle: 'none', // IE and Edge
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari and Opera
  },
}));

const StoreList = styled(List)(({ theme }) => ({
  maxHeight: '50vh',
  overflow: 'auto',
  scrollbarWidth: 'none', // Firefox
  msOverflowStyle: 'none', // IE and Edge
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari and Opera
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(45deg, #5e35b1 30%, #1e88e5 90%)',
  color: 'white',
  padding: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(2),
}));

const MainContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const BookCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s',
  width: '100%',
  margin: '0 auto',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: theme.shadows[6],
  },
}));

const StyledBadge = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 10,
  right: 10,
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  padding: '4px 8px',
  borderRadius: 12,
  fontSize: '0.75rem',
  fontWeight: 'bold',
}));

const SearchBarHeader = styled(Paper)(({ theme }) => ({
  position: 'relative',
  minHeight: '60px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[4],
  marginBottom: theme.spacing(3),
  padding: theme.spacing(3),
  overflow: 'hidden',
  zIndex: 1,
}));

const SearchOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'linear-gradient(45deg, rgba(94, 53, 177, 0.82) 30%, rgba(30, 136, 229, 0.72) 90%)',
  zIndex: -1,
}));

// Default logo component for book stores
const DefaultStoreLogo = styled(Box)(({ theme }) => ({
  width: 20,
  height: 20,
  backgroundColor: '#e3f2fd',
  borderRadius: 4,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #bbdefb',
}));

const bazars = [
  { bn: 'সকল বাজার', en: 'All Bazars' },
  { bn: 'চাচকৈড় বাজার', en: 'Chachkoir Bazar' },
  { bn: 'খুবজিপুর বাজার', en: 'Khubipur Bazar' },
  { bn: 'নাজিরপুর বাজার', en: 'Nazirpur Bazar' },
  { bn: 'বিলদোহর বাজার', en: 'Bildohar Bazar' },
  { bn: 'চাটখিল বাজার', en: 'Chatkhil Bazar' },
  { bn: 'সোনাইমুড়ি বাজার', en: 'Sonaimuri Bazar' },
  { bn: 'কেন্দ্রীয় বাজার', en: 'Central Bazar' },
  { bn: 'নিউ মার্কেট', en: 'New Market' },
  { bn: 'গুলিস্তান বাজার', en: 'Gulistan Bazar' },
  { bn: 'মৌচাক বাজার', en: 'Mouchak Bazar' },
  { bn: 'রকেট বাজার', en: 'Rocket Bazar' },
  { bn: 'কাওরান বাজার', en: 'Kawran Bazar' },
  { bn: 'ফার্মগেট বাজার', en: 'Farmgate Bazar' },
  { bn: 'মিরপুর বাজার', en: 'Mirpur Bazar' },
  { bn: 'উত্তরা বাজার', en: 'Uttara Bazar' },
  { bn: 'ধানমন্ডি বাজার', en: 'Dhanmondi Bazar' },
  { bn: 'মোহাম্মদপুর বাজার', en: 'Mohammadpur Bazar' },
  { bn: 'বনানী বাজার', en: 'Banani Bazar' },
  { bn: 'গুলশান বাজার', en: 'Gulshan Bazar' }
];

// Book categories
const books = [
  {
    category: { bangla: "সাহিত্য", english: "Literature" },
    icon: <MenuBook />,
    items: [
      { bangla: "উপন্যাস", english: "Novel", icon: <MenuBook /> },
      { bangla: "গল্প", english: "Short Stories", icon: <MenuBook /> },
      { bangla: "কবিতা", english: "Poetry", icon: <MenuBook /> },
      { bangla: "প্রবন্ধ", english: "Essay", icon: <MenuBook /> },
      { bangla: "অনুবাদ সাহিত্য", english: "Translated Literature", icon: <MenuBook /> }
    ]
  },
  {
    category: { bangla: "শিক্ষা ও একাডেমিক", english: "Educational & Academic" },
    icon: <School />,
    items: [
      { bangla: "প্রাথমিক শিক্ষা", english: "Primary Education", icon: <School /> },
      { bangla: "মাধ্যমিক শিক্ষা", english: "Secondary Education", icon: <School /> },
      { bangla: "উচ্চ মাধ্যমিক", english: "Higher Secondary", icon: <School /> },
      { bangla: "বিশ্ববিদ্যালয় শিক্ষা", english: "University Level", icon: <School /> },
      { bangla: "প্রশ্নব্যাংক", english: "Question Bank", icon: <School /> }
    ]
  },
  {
    category: { bangla: "ইসলামী বই", english: "Islamic Books" },
    icon: <AutoStories />,
    items: [
      { bangla: "কুরআন", english: "Qur'an", icon: <AutoStories /> },
      { bangla: "হাদিস", english: "Hadith", icon: <AutoStories /> },
      { bangla: "ইসলামী জীবনদর্শন", english: "Islamic Philosophy", icon: <AutoStories /> },
      { bangla: "ইসলামী ইতিহাস", english: "Islamic History", icon: <AutoStories /> }
    ]
  },
  {
    category: { bangla: "শিশু ও কিশোর সাহিত্য", english: "Children & Teens" },
    icon: <Book />,
    items: [
      { bangla: "ছড়া", english: "Rhymes", icon: <Book /> },
      { bangla: "ছোটদের গল্প", english: "Children's Stories", icon: <Book /> },
      { bangla: "কিশোর উপন্যাস", english: "Teen Novels", icon: <Book /> },
      { bangla: "শিক্ষামূলক বই", english: "Educational Books", icon: <Book /> }
    ]
  },
  {
    category: { bangla: "আত্ম-উন্নয়ন", english: "Self-Help & Motivation" },
    icon: <Psychology />,
    items: [
      { bangla: "আত্ম-উন্নয়ন", english: "Personal Development", icon: <Psychology /> },
      { bangla: "মোটিভেশন", english: "Motivation", icon: <Psychology /> },
      { bangla: "মানসিক স্বাস্থ্য", english: "Mental Health", icon: <Psychology /> }
    ]
  },
  {
    category: { bangla: "ভ্রমণ ও ভৌগলিক বই", english: "Travel & Geography" },
    icon: <TravelExplore />,
    items: [
      { bangla: "ভ্রমণ কাহিনী", english: "Travelogue", icon: <TravelExplore /> },
      { bangla: "বাংলাদেশ ভ্রমণ", english: "Travel in Bangladesh", icon: <TravelExplore /> },
      { bangla: "বিশ্ব ভ্রমণ", english: "World Travel", icon: <TravelExplore /> }
    ]
  },
  {
    category: { bangla: "বিজ্ঞান ও প্রযুক্তি", english: "Science & Technology" },
    icon: <Science />,
    items: [
      { bangla: "পদার্থবিজ্ঞান", english: "Physics", icon: <Science /> },
      { bangla: "রসায়ন", english: "Chemistry", icon: <Science /> },
      { bangla: "জীববিজ্ঞান", english: "Biology", icon: <Science /> },
      { bangla: "কম্পিউটার", english: "Computer Science", icon: <Science /> }
    ]
  },
  {
    category: { bangla: "আইন ও বিচার", english: "Law & Justice" },
    icon: <Gavel />,
    items: [
      { bangla: "বাংলাদেশের আইন", english: "Bangladeshi Law", icon: <Gavel /> },
      { bangla: "আন্তর্জাতিক আইন", english: "International Law", icon: <Gavel /> },
      { bangla: "মানবাধিকার", english: "Human Rights", icon: <Gavel /> }
    ]
  },
  {
    category: { bangla: "রোমান্স ও ভালোবাসা", english: "Romance & Love" },
    icon: <Favorite />,
    items: [
      { bangla: "রোমান্টিক উপন্যাস", english: "Romantic Novels", icon: <Favorite /> },
      { bangla: "ভালোবাসার গল্প", english: "Love Stories", icon: <Favorite /> }
    ]
  },
  {
    category: { bangla: "ইতিহাস ও রাজনীতি", english: "History & Politics" },
    icon: <Public />,
    items: [
      { bangla: "বাংলার ইতিহাস", english: "History of Bengal", icon: <Public /> },
      { bangla: "বাংলাদেশের রাজনীতি", english: "Politics of Bangladesh", icon: <Public /> },
      { bangla: "বিশ্ব রাজনীতি", english: "World Politics", icon: <Public /> }
    ]
  },
  {
    category: { bangla: "দর্শন ও চিন্তাধারা", english: "Philosophy & Thought" },
    icon: <EmojiObjects />,
    items: [
      { bangla: "পশ্চিমা দর্শন", english: "Western Philosophy", icon: <EmojiObjects /> },
      { bangla: "প্রাচ্য দর্শন", english: "Eastern Philosophy", icon: <EmojiObjects /> },
      { bangla: "নৈতিক শিক্ষা", english: "Ethics", icon: <EmojiObjects /> }
    ]
  }
];

// List of book stores
const stores = [
  {
    name: 'বাতিঘর',
    logo: '/images/logos/batighor.png',
    description: 'বইপ্রেমীদের প্রিয় ঠিকানা'
  },
  {
    name: 'রকমারি',
    logo: '/images/logos/rokomari.png',
    description: 'বাংলাদেশের সবচেয়ে বড় বইয়ের দোকান'
  },
  {
    name: 'প্রথমা প্রকাশন',
    logo: '/images/logos/prothoma.png',
    description: 'উন্নতমানের বই প্রকাশনা'
  },
  {
    name: 'অন্যপ্রকাশ',
    logo: '/images/logos/onyprokash.png',
    description: 'আধুনিক বাংলা সাহিত্যের নির্ভরযোগ্য প্রকাশনা'
  },
  {
    name: 'আদর্শ',
    logo: '/images/logos/adorso.png',
    description: 'ঐতিহ্যবাহী বই প্রকাশনা'
  },
  {
    name: 'সময় প্রকাশন',
    logo: '/images/logos/somoy.png',
    description: 'সময়ের সেরা বই'
  }
];

// Sample books data
const booksData = [
  {
    id: 1,
    name: 'পথের পাঁচালী',
    englishName: 'Pather Panchali',
    category: 'উপন্যাস',
    store: 'বাতিঘর',
    price: '৳৩৫০.০০',
    image: '/images/books/b1.jpg',
    description: 'বিভূতিভূষণ বন্দ্যোপাধ্যায়ের বিখ্যাত উপন্যাস',
    author: 'বিভূতিভূষণ বন্দ্যোপাধ্যায়'
  },
  {
    id: 2,
    name: 'ফেলুদা সমগ্র',
    englishName: 'Complete Feluda',
    category: 'গল্প',
    store: 'রকমারি',
    price: '৳৮৫০.০০',
    image: '/images/books/b2.jpg',
    description: 'সত্যজিৎ রায়ের গোয়েন্দা গল্পের সংকলন',
    author: 'সত্যজিৎ রায়'
  },
  {
    id: 3,
    name: 'শেষের কবিতা',
    englishName: 'Last Poem',
    category: 'কবিতা',
    store: 'প্রথমা প্রকাশন',
    price: '৳৪৫০.০০',
    image: '/images/books/b3.jpg',
    description: 'রবীন্দ্রনাথ ঠাকুরের বিখ্যাত কাব্যগ্রন্থ',
    author: 'রবীন্দ্রনাথ ঠাকুর'
  },
  {
    id: 4,
    name: 'আত্মজীবনী',
    englishName: 'Autobiography',
    category: 'প্রবন্ধ',
    store: 'অন্যপ্রকাশ',
    price: '৳৫৫০.০০',
    image: '/images/books/b4.jpg',
    description: 'শেখ মুজিবুর রহমানের আত্মজীবনী',
    author: 'শেখ মুজিবুর রহমান'
  },
  {
    id: 5,
    name: 'মধ্যাহ্ন',
    englishName: 'Midday',
    category: 'উপন্যাস',
    store: 'আদর্শ',
    price: '৳২৫০.০০',
    image: '/images/books/b5.jpg',
    description: 'হুমায়ূন আহমেদের জনপ্রিয় উপন্যাস',
    author: 'হুমায়ূন আহমেদ'
  },
  {
    id: 6,
    name: 'লাল সালু',
    englishName: 'Lal Salu',
    category: 'উপন্যাস',
    store: 'সময় প্রকাশন',
    price: '৳৩০০.০০',
    image: '/images/books/b6.jpg',
    description: 'সৈয়দ ওয়ালীউল্লাহর বিখ্যাত উপন্যাস',
    author: 'সৈয়দ ওয়ালীউল্লাহ'
  }
];

  // Typing animation effect
  const placeholderTexts = [
    'বইয়ের নাম লিখুন...',
    'লেখকের নাম লিখুন...',
    'দোকানের নাম লিখুন...',
    'ক্যাটেগরির নাম লিখুন...',
    'বইয়ের ধরন লিখুন...'
  ];

const BookBazar = () => {
  const { addToCart } = useCart();
  const [searchQuery, setSearchQuery] = useState('');
  const [typingText, setTypingText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [typingIndex, setTypingIndex] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [selectedStore, setSelectedStore] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('সকল জেলা');
  const [selectedUpazilla, setSelectedUpazilla] = useState('সকল উপজেলা');
  const [selectedBazar, setSelectedBazar] = useState('সকল বাজার');
  const [districtSearchQuery, setDistrictSearchQuery] = useState('');
  const [upazillaSearchQuery, setUpazillaSearchQuery] = useState('');
  const [bazarSearchQuery, setBazarSearchQuery] = useState('');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed, setTypingSpeed] = useState(150);
  const [expandedCategories, setExpandedCategories] = useState({});



  // Inject keyframes for the blinking cursor animation
  useEffect(() => {
    const cleanup = injectGlobalStyles();
    return cleanup;
  }, []);

  // Typewriter effect for search placeholders
  useEffect(() => {
    const currentPlaceholder = placeholderTexts[placeholderIndex];
    
    const handleTyping = () => {
      if (isDeleting) {
        setTypingText(prev => prev.slice(0, -1));
        setTypingSpeed(50);
        
        if (typingText === '') {
          setIsDeleting(false);
          setPlaceholderIndex((prev) => (prev + 1) % placeholderTexts.length);
          setTypingSpeed(150);
        }
      } else {
        setTypingText(currentPlaceholder.slice(0, typingText.length + 1));
        setTypingSpeed(150);
        
        if (typingText === currentPlaceholder) {
          setTypingSpeed(2000);
          setIsDeleting(true);
        }
      }
    };
    
    const timer = setTimeout(handleTyping, typingSpeed);
    return () => clearTimeout(timer);
  }, [typingText, isDeleting, placeholderIndex, placeholderTexts, typingSpeed]);

  const handleCategoryClick = (categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  const handleSubCategoryClick = (mainCategoryKey, subCategory) => {
    setSelectedCategory(mainCategoryKey);
    setSelectedSubCategory(subCategory);
  };

  const handleStoreClick = (store) => {
    setSelectedStore(store.name);
  };

  const handleDistrictChange = (event) => {
    setSelectedDistrict(event.target.value);
  };

  const handleUpazillaChange = (event) => {
    setSelectedUpazilla(event.target.value);
  };

  const handleBazarChange = (event) => {
    setSelectedBazar(event.target.value);
  };

  const handleAddToCart = (product) => {
    console.log('BookBazar - Adding product to cart:', product);
    
    const cartProduct = {
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      store: product.store,
      stock: product.stock || 10
    };
    
    addToCart(cartProduct, 1);
  };

  const filteredBooks = booksData.filter((book) => {
    const matchesCategory = !selectedCategory || 
      (selectedSubCategory ? book.category === selectedSubCategory : 
       books.find(cat => cat.category.bangla === selectedCategory)?.items.some(item => item.bangla === book.category));
    const matchesStore = !selectedStore || book.store === selectedStore;
    const matchesSearch = !searchQuery || 
      book.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      book.englishName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      book.author.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesStore && matchesSearch;
  });

  // Filter bazars based on search query
  const filteredBazars = bazars.filter(
    bazar => bazar.bn.toLowerCase().includes(bazarSearchQuery.toLowerCase())
  );

  return (
    <>
      <Header />
      <Box sx={{ flexGrow: 1, p: 2, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
        {/* Search Header Bar */}
        <SearchBarHeader>
          <SearchOverlay />          {/* Location Selector */}
          <LocationSelector 
            variant="search-bar"
            onLocationChange={(location) => {
              // Optional: Handle location change if needed
              console.log('Location changed:', location);
            }}
          />

          {/* Animated Search Field */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder={typingText}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ 
              mx: 2, 
              backgroundColor: 'rgba(255, 255, 255, 0.92)',
              borderRadius: 2,
              boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              },
              zIndex: 2,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Box sx={{ 
                    opacity: isDeleting ? 0 : 1, 
                    transition: 'opacity 0.3s',
                    height: '1.2em', 
                    width: '0.15em', 
                    backgroundColor: 'primary.main',
                    display: 'inline-block',
                    animation: 'blink 1s step-end infinite'
                  }}/>
                </InputAdornment>
              )
            }}
          />
        </SearchBarHeader>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Left Sidebar */}
          <Box sx={{ 
            width: { xs: '100%', md: '260px' }, 
            minWidth: { md: '260px' },
            pr: { md: 2 }, 
            mb: { xs: 2, md: 0 } 
          }}>
            <Sidebar elevation={3}>
              <Box sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <SectionHeader>
                  <MenuBook sx={{ mr: 1 }} />
                  <Typography variant="h6" component="div">
                    বইয়ের ধরন
                  </Typography>
                </SectionHeader>
                <CategoryList sx={{ pl: 1, pr: 1 }}>
                  {books.map((categoryData) => {
                    const categoryKey = categoryData.category.bangla;
                    return (
                      <React.Fragment key={categoryKey}>
                        <ListItem
                          button
                          onClick={() => handleCategoryClick(categoryKey)}
                          selected={selectedCategory === categoryKey}
                          sx={{ py: 1 }}
                        >
                          <ListItemIcon>
                            {categoryData.icon}
                          </ListItemIcon>
                          <ListItemText 
                            primary={categoryData.category.bangla}
                            secondary={categoryData.category.english}
                            primaryTypographyProps={{ 
                              style: { 
                                fontWeight: selectedCategory === categoryKey ? 'bold' : 'normal',
                                fontSize: '0.9rem'
                              } 
                            }}
                            secondaryTypographyProps={{ 
                              style: { 
                                fontSize: '0.75rem',
                                color: 'text.secondary'
                              } 
                            }}
                          />
                          {expandedCategories[categoryKey] ? <ExpandLess /> : <ExpandMore />}
                        </ListItem>
                        <Collapse in={expandedCategories[categoryKey]} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding>
                            {categoryData.items.map((item) => (
                              <ListItem
                                button
                                key={`${categoryKey}-${item.bangla}`}
                                sx={{ pl: 4, py: 0.5 }}
                                selected={selectedCategory === categoryKey && selectedSubCategory === item.bangla}
                                onClick={() => handleSubCategoryClick(categoryKey, item.bangla)}
                              >
                                <ListItemIcon>
                                  {item.icon}
                                </ListItemIcon>
                                <ListItemText 
                                  primary={item.bangla}
                                  secondary={item.english}
                                  primaryTypographyProps={{ 
                                    style: { 
                                      fontSize: '0.8rem',
                                      fontWeight: selectedCategory === categoryKey && selectedSubCategory === item.bangla ? 'bold' : 'normal'
                                    } 
                                  }}
                                  secondaryTypographyProps={{ 
                                    style: { fontSize: '0.7rem' } 
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Collapse>
                      </React.Fragment>
                    );
                  })}
                </CategoryList>

                <SectionHeader sx={{ mt: 2 }}>
                  <Store sx={{ mr: 1 }} />
                  <Typography variant="h6" component="div">
                    দোকানের তালিকা
                  </Typography>
                </SectionHeader>
                <StoreList sx={{ pl: 1, pr: 1 }}>
                  {stores.map((store) => (
                    <ListItem
                      button
                      key={store.name}
                      selected={selectedStore === store.name}
                      onClick={() => handleStoreClick(store)}
                      sx={{ py: 1 }}
                    >
                      <ListItemIcon sx={{ minWidth: 35 }}>
                        <DefaultStoreLogo>
                          <Store sx={{ fontSize: 12, color: '#1976d2' }} />
                        </DefaultStoreLogo>
                      </ListItemIcon>
                      <ListItemText 
                        primary={store.name}
                        secondary={store.description}
                        primaryTypographyProps={{ 
                          fontSize: '0.875rem',
                          fontWeight: selectedStore === store.name ? 'bold' : 'normal' 
                        }}
                        secondaryTypographyProps={{ 
                          fontSize: '0.75rem',
                          color: 'text.secondary'
                        }}
                      />
                    </ListItem>
                  ))}
                </StoreList>
              </Box>
            </Sidebar>
          </Box>

          {/* Main Content */}
          <Box sx={{ 
            width: { xs: '100%', md: 'calc(100% - 260px)' },
            flexGrow: 1 
          }}>
            <MainContent>
              {/* Books Grid */}
              {filteredBooks.length > 0 ? (
                <Box
                  sx={{
                    display: 'grid',
                    gap: 3,
                    gridTemplateColumns: {
                      xs: 'repeat(1, 1fr)', // Mobile portrait: 1 card per row
                      sm: 'repeat(2, 1fr)', // Mobile horizontal: 2 cards per row
                      md: 'repeat(5, 1fr)', // Laptop: 5 cards per row
                      lg: 'repeat(6, 1fr)', // Big screen: 6 cards per row
                      '@media (min-width: 1980px)': {
                        gridTemplateColumns: 'repeat(9, 1fr)' // Extra large screens: 9 cards per row
                      }
                    },
                    width: '100%',
                  }}
                >
                  {filteredBooks.map((book) => (
                    <Box key={book.id} sx={{ display: 'flex', width: '100%' }}>
                      <BookCard sx={{ width: '100%' }}>
                        <Box sx={{ position: 'relative' }}>
                          <CardMedia
                            component="img"
                            height="160"
                            image={book.image}
                            alt={book.name}
                            sx={{ objectFit: 'contain', backgroundColor: '#f5f5f5', pt: 1 }}
                            onError={(e) => {
                              e.target.src = '/images/placeholder.png';
                            }}
                          />
                          <StyledBadge>{book.category}</StyledBadge>
                        </Box>
                        <CardContent sx={{
                          flexGrow: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                          pb: '16px !important' // Override default padding bottom
                        }}>
                          <Box>
                            <Typography gutterBottom variant="h6" component="div">
                              {book.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {book.englishName}
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="body2" color="text.primary">
                                <strong>লেখক:</strong> {book.author}
                              </Typography>
                              <Typography variant="body2" color="text.primary">
                                <strong>দোকান:</strong> {book.store}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{
                            mt: 'auto', // Push this box to the bottom
                            pt: 2 // Add some spacing from the content above
                          }}>
                            <Typography variant="h6" color="primary" sx={{ mb: 1, textAlign: 'center' }}>
                              {book.price}
                            </Typography>
                            <Box sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 1
                            }}>
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                fullWidth
                              >
                                কিনুন
                              </Button>
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                fullWidth
                                onClick={() => handleAddToCart(book)}
                              >
                                ব্যাগে নিন
                              </Button>
                            </Box>
                          </Box>
                        </CardContent>
                      </BookCard>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
                  <MenuBook sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    কোন বই পাওয়া যায়নি
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    অনুগ্রহ করে আপনার ফিল্টার বা সার্চ পরিবর্তন করুন
                  </Typography>
                  <Button 
                    variant="outlined" 
                    sx={{ mt: 2 }}
                    onClick={() => {
                      setSelectedCategory('');
                      setSelectedSubCategory('');
                      setSelectedStore('');
                      setSearchQuery('');
                    }}
                  >
                    সব ফিল্টার মুছুন
                  </Button>
                </Paper>
              )}
            </MainContent>
          </Box>
        </Box>
      </Box>
      <Box sx={{ mt: "50px" }}>
        <Footer />
      </Box>
    </>
  );
};

export default BookBazar;
